import {
  Limits,
  MetricUsage,
  ProjectQuotaLimits,
  UsageMetricMetadata,
  Page,
  MessageTypeOptions,
  APIError,
  handleApiError,
  CommonAPI,
  SessionStats,
  SessionTokenUsage,
  MomentUsage,
  SessionXAxis,
  SessionRange,
  SessionTreeNode,
  AgentCloneTree,
  AgencyTagType,
  RatingType,
  RateMomentResponse,
} from "@bagel-web/common";

import type {
  AgentDetails,
  APIRequestSummary,
  ChalkLogListItem,
  FullChalkLog,
  Organization,
  OrganizationSummary,
  Project,
  AgentState,
  AgentPresentation,
  APIKey,
  NewProject,
  EditProject,
  MessageStreamMomentSummary,
  MessageStreamMomentDetail,
  AgentMomentList,
  APIRequestDetails,
  ServiceAlias,
  BackendServiceMap,
  EditServiceAlias,
  OrganizationService,
  CreateOrganization,
  CreatedOrganization,
  InviteOrganizationUser,
  PagedModelUsage,
  PagedModelUsageBreakdown,
  AuthZProject,
  AdminProjectUser,
  AddProjectUser,
} from "./types";
import { SessionDetails, SessionUsage } from "types/session";

function buildQueryParams(
  obj: Record<
    string,
    | string
    | number
    | boolean
    | Array<string | number | boolean | null | undefined>
    | null
    | undefined
  >
) {
  const params: Array<string> = [];

  Object.keys(obj)
    .filter((k) => obj[k] !== undefined && obj[k] !== null)
    .forEach((k) => {
      const v = obj[k];
      if (Array.isArray(v)) {
        v.forEach((item) => {
          const encodedValue =
            item !== null && item !== undefined ? encodeURIComponent(item) : "";
          params.push(`${encodeURIComponent(k)}=${encodedValue}`);
        });
      } else {
        const encodedValue =
          v !== null && v !== undefined ? encodeURIComponent(v) : "";
        params.push(`${encodeURIComponent(k)}=${encodedValue}`);
      }
    });

  return params.join("&");
}

export function buildQueryString(
  uri: string,
  obj: Record<
    string,
    | string
    | number
    | boolean
    | Array<string | number | boolean | null | undefined>
    | null
    | undefined
  >
) {
  const queryString = buildQueryParams(obj);
  return queryString && queryString !== "" ? `${uri}?${queryString}` : uri;
}

async function parseError(res: Response): Promise<APIError> {
  try {
    const json = await res.json();

    if ("error" in json) {
      return new APIError({
        error: json.error,
        status: res.status,
        headers: res.headers,
      });
    }
  } catch (e) {
    // fall back to a generic error.
  }

  return new APIError({
    error: {
      type: "unknown",
      message: `Unexpected response status ${res.status}.`,
      trace: res.headers.get("X-trace-id") ?? "",
    },
    status: res.status,
    headers: res.headers,
  });
}

export async function getAPI<T>(
  url: string,
  init?: { signal?: AbortSignal | null }
): Promise<T> {
  const res = await fetch(url, init);

  if (res.ok) return res.json();
  else return handleApiError(await parseError(res));
}

export async function postAPI<T>(
  url: string,
  body: object | string,
  options?: {
    headers?: object;
    init?: { signal?: AbortSignal | null };
  }
): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...options?.headers },
    body: JSON.stringify(body),
    ...options?.init,
  });

  if (res.ok) {
    try {
      return await res.json();
    } catch (e) {
      return undefined as T;
    }
  } else return handleApiError(await parseError(res));
}

export async function patchAPI<T>(
  url: string,
  body: object,
  init?: { signal?: AbortSignal | null }
): Promise<T> {
  const res = await fetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...init,
  });

  if (res.ok) {
    try {
      return await res.json();
    } catch (e) {
      return undefined as T;
    }
  } else return handleApiError(await parseError(res));
}

export async function putAPI<T>(
  url: string,
  body: object,
  init?: { signal?: AbortSignal | null }
): Promise<T | undefined> {
  const res = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...init,
  });

  if (res.ok) {
    try {
      return await res.json();
    } catch (e) {
      return undefined;
    }
  } else return handleApiError(await parseError(res));
}

export async function deleteAPI<T>(
  url: string,
  init?: { signal?: AbortSignal | null }
): Promise<T | undefined> {
  const res = await fetch(url, { method: "DELETE", ...init });
  if (res.ok) {
    try {
      return await res.json();
    } catch (e) {
      return undefined;
    }
  } else return handleApiError(await parseError(res));
}

export async function fetchChalkLogs({
  sessionId,
  startsAfter,
}: {
  sessionId: string;
  startsAfter?: string | null;
}): Promise<Page<ChalkLogListItem>> {
  const url = "/api/chalk-logs";
  return getAPI(
    buildQueryString(url, {
      session_key: sessionId,
      starting_after: startsAfter,
    })
  );
}

export async function fetchChalkLog(
  sessionId: string,
  chalkLogId: string
): Promise<FullChalkLog> {
  const url = `/api/chalk-logs/${sessionId}/${chalkLogId}`;
  return getAPI(url);
}

export async function fetchChalkLogById(
  chalkLogId: string
): Promise<FullChalkLog> {
  const url = `/api/chalk-logs/${chalkLogId}`;
  return getAPI(url);
}

export async function fetchModels(): Promise<Array<string>> {
  return getAPI("/api/models");
}

export function fetchSession(sessionId: string): Promise<SessionDetails> {
  return getAPI(`/api/sessions/${sessionId}`);
}

export function fetchSessionUsage(
  sessionId: string
): Promise<Array<SessionUsage>> {
  return getAPI(`/api/sessions/${sessionId}/usage`);
}

export function fetchAgentDetails({
  sessionId,
  agentId,
}: {
  sessionId: string;
  agentId: string;
}): Promise<AgentDetails> {
  return getAPI(`/api/sessions/${sessionId}/agents/${agentId}`);
}

export function fetchAgentPublicState({
  sessionId,
  agentId,
  momentId,
}: {
  sessionId: string;
  agentId: string;
  momentId?: number;
}): Promise<AgentState> {
  return momentId !== undefined
    ? getAPI(`/api/sessions/${sessionId}/agents/${agentId}/state/${momentId}`)
    : getAPI(`/api/sessions/${sessionId}/agents/${agentId}/state`);
}

export function fetchAgentPresentation({
  sessionId,
  agentId,
  momentId,
}: {
  sessionId: string;
  agentId: string;
  momentId?: number;
}): Promise<AgentPresentation> {
  return momentId !== undefined
    ? getAPI(
        `/api/sessions/${sessionId}/agents/${agentId}/presentation/${momentId}`
      )
    : getAPI(`/api/sessions/${sessionId}/agents/${agentId}/presentation`);
}

export async function createOrganization(
  organization: CreateOrganization
): Promise<CreatedOrganization> {
  return postAPI("/api/organizations", organization);
}

export async function inviteOrganizationUser(
  organizationId: string,
  invite: InviteOrganizationUser
): Promise<void> {
  return postAPI(`/api/organizations/${organizationId}/invitations`, invite);
}

export async function syncToWorkOS(organizationId: string): Promise<void> {
  return postAPI(`/api/organizations/${organizationId}/workos-sync`, {});
}

export async function syncProjectsToWorkOS(
  organizationId: string
): Promise<Page<Project>> {
  return postAPI(
    `/api/organizations/${organizationId}/projects/workos-sync`,
    {}
  );
}

export async function fetchOrganizations(): Promise<Page<OrganizationSummary>> {
  return getAPI("/api/organizations");
}

export async function fetchOrganization(
  organization: string
): Promise<Organization> {
  const url = `/api/organizations/${organization}`;
  return getAPI(url);
}

export async function fetchOrganizationLimits(
  organization: string
): Promise<Limits> {
  const url = `/api/organizations/${organization}/limits`;
  return getAPI(url);
}

export async function fetchOrganizationUsageRecent({
  organization,
  beginsWith,
}: {
  organization: string;
  beginsWith?: string;
}): Promise<MetricUsage[]> {
  return getAPI(
    buildQueryString(`/api/organizations/${organization}/usage-counts`, {
      begins_with: beginsWith || undefined,
    })
  );
}

export async function fetchOrganizationUsageBetween({
  organization,
  startDate,
  endDate,
}: {
  organization: string;
  startDate: string;
  endDate: string;
}): Promise<MetricUsage[]> {
  return getAPI(
    buildQueryString(`/api/organizations/${organization}/usage-counts`, {
      between: `daily-${startDate},daily-${endDate}`,
    })
  );
}

export async function patchOrganizationLimits(
  organization: string,
  limits: Limits
): Promise<void> {
  const url = `/api/organizations/${organization}/limits`;
  return await patchAPI(url, limits);
}

export async function fetchUsageMetrics(): Promise<UsageMetricMetadata[]> {
  return getAPI("/api/usage-metrics");
}

export async function fetchOrganizationProjectLimits(
  organization: string,
  projectId: string
): Promise<ProjectQuotaLimits> {
  return getAPI(
    `/api/organizations/${organization}/projects/${projectId}/limits`
  );
}

export async function patchOrganizationProjectLimits(
  organization: string,
  projectId: string,
  limits: ProjectQuotaLimits
): Promise<void> {
  return patchAPI(
    `/api/organizations/${organization}/projects/${projectId}/limits`,
    limits
  );
}

export async function fetchProjectUsageRecent({
  organization,
  projectId,
  beginsWith,
}: {
  organization: string;
  projectId: string;
  beginsWith?: string;
}): Promise<MetricUsage[]> {
  return getAPI(
    buildQueryString(
      `/api/organizations/${organization}/projects/${projectId}/usage-counts`,
      {
        begins_with: beginsWith || undefined,
      }
    )
  );
}

export async function fetchProjectUsageBetween({
  organization,
  projectId,
  startDate,
  endDate,
}: {
  organization: string;
  projectId: string;
  startDate: string;
  endDate: string;
}): Promise<MetricUsage[]> {
  return getAPI(
    buildQueryString(
      `/api/organizations/${organization}/projects/${projectId}/usage-counts`,
      {
        between: `daily-${startDate},daily-${endDate}`,
      }
    )
  );
}

export type APIRequestSearchParams = {
  organization?: string | null;
  fromDateTime?: number | null;
  toDateTime?: number | null;
};

export async function fetchOrganizationAPIKeys({
  organization,
  notes,
  startsAfter = null,
}: {
  organization: string;
  notes?: string;
  startsAfter: string | null;
}): Promise<Page<APIKey>> {
  return getAPI(
    buildQueryString(`/api/api-keys/${organization}`, {
      notes: notes || undefined,
      starting_after: startsAfter || undefined,
    })
  );
}

export async function fetchOrganizationProjectUsers({
  organizationId,
  projectId,
}: {
  organizationId: string;
  projectId: string;
}): Promise<Page<AdminProjectUser>> {
  return getAPI(
    `/api/organizations/${organizationId}/projects/${projectId}/users`
  );
}

export async function addUserToOrganizationProject({
  organizationId,
  projectId,
  user,
}: {
  organizationId: string;
  projectId: string;
  user: AddProjectUser;
}): Promise<void> {
  return postAPI(
    `/api/organizations/${organizationId}/projects/${projectId}/users`,
    user
  );
}

export async function removeUserFromOrganizationProject({
  organizationId,
  projectId,
  userId,
}: {
  organizationId: string;
  projectId: string;
  userId: string;
}): Promise<void> {
  return deleteAPI(
    `/api/organizations/${organizationId}/projects/${projectId}/users/${userId}`
  );
}

export async function fetchOrganizationProject({
  organization,
  projectId,
}: {
  organization: string;
  projectId: string;
}): Promise<Project | AuthZProject> {
  return getAPI(`/api/organizations/${organization}/projects/${projectId}`);
}

export async function fetchOrganizationProjects({
  includeArchived = false,
  organization,
  startsAfter = null,
}: {
  includeArchived?: boolean;
  organization: string;
  startsAfter?: string | null;
}): Promise<Page<Project>> {
  return getAPI(
    buildQueryString(`/api/organizations/${organization}/projects`, {
      include_archived: includeArchived || undefined,
      starting_after: startsAfter || undefined,
    })
  );
}

export async function createProject({
  organization,
  project,
}: {
  organization: string;
  project: NewProject;
}): Promise<void> {
  const url = `/api/organizations/${organization}/projects`;
  return postAPI(url, project);
}

export async function patchOrganizationProject({
  organization,
  project,
}: {
  organization: string;
  project: EditProject;
}): Promise<void> {
  const url = `/api/organizations/${organization}/projects/${project.id}`;
  return patchAPI(url, project);
}

export async function archiveOrganizationProject({
  organization,
  project,
}: {
  organization: string;
  project: EditProject;
}): Promise<void> {
  const url = `/api/organizations/${organization}/projects/${project.id}/archive`;
  return postAPI(url, {});
}

export async function unarchiveOrganizationProject({
  organization,
  project,
}: {
  organization: string;
  project: EditProject;
}): Promise<void> {
  const url = `/api/organizations/${organization}/projects/${project.id}/unarchive`;
  return postAPI(url, {});
}

export async function fetchAPIRequests({
  startsAfter = null,
  onlyFailed = false,
  organization,
  projectId,
  agentId,
  apiKeyId,
  sessionId,
  fromDateTime,
  toDateTime,
}: {
  startsAfter: string | null;
  onlyFailed: boolean;
  projectId?: string;
  organization?: string;
  agentId?: string;
  apiKeyId?: string;
  sessionId?: string;
  fromDateTime?: number;
  toDateTime?: number;
}): Promise<Page<APIRequestSummary>> {
  const url = buildQueryString("/api/api-requests", {
    starting_after: startsAfter,
    only_failed: onlyFailed,
    organization: organization,
    project_id: projectId,
    agent_id: agentId,
    session_id: sessionId,
    api_key_id: apiKeyId,
    from_date_time: fromDateTime,
    to_date_time: toDateTime,
  });

  return getAPI(url);
}

export async function fetchAllProjects(): Promise<Page<Project>> {
  return getAPI("/api/projects");
}

export function fetchEventStream({
  sessionId,
  agentId,
  reverse = false,
  startsAfter = null,
}: {
  sessionId: string;
  agentId: string;
  reverse?: boolean;
  startsAfter?: string | null;
}): Promise<Page<MessageStreamMomentSummary>> {
  return getAPI(
    buildQueryString(
      `/api/sessions/${sessionId}/agents/${agentId}/event-stream`,
      { reverse, starting_after: startsAfter }
    )
  );
}

export function fetchEventStreamDetail(
  sessionId: string,
  agentId: string,
  momentId: number
): Promise<MessageStreamMomentDetail> {
  return getAPI(
    `/api/sessions/${sessionId}/agents/${agentId}/event-stream/${momentId}`
  );
}

export function fetchMessageTypes(): Promise<MessageTypeOptions> {
  return getAPI("/api/message-types");
}

export function fetchAgentMomentList({
  sessionId,
  agentId,
  startTime,
  endTime,
  functionName,
  hasError,
}: {
  sessionId: string;
  agentId: string;
  startTime?: number;
  endTime?: number;
  functionName?: string;
  hasError?: boolean;
}): Promise<AgentMomentList> {
  return getAPI(
    buildQueryString(`/api/sessions/${sessionId}/agents/${agentId}/moments`, {
      start_time: startTime,
      end_time: endTime,
      function_called: functionName,
      has_llm_failure: hasError,
    })
  );
}

export function fetchAPIRequest({
  organization,
  requestId,
}: {
  organization: string;
  requestId: string;
}): Promise<APIRequestDetails> {
  return getAPI(`/api/api-requests/${organization}/${requestId}`);
}

export function fetchSessionStats({
  sessionId,
  startTime,
  endTime,
  numBuckets,
  xAxis,
}: {
  sessionId: string;
  startTime?: number;
  endTime?: number;
  numBuckets?: number;
  xAxis?: SessionXAxis;
}): Promise<SessionStats> {
  return getAPI(
    buildQueryString(`/api/sessions/${sessionId}/stats`, {
      start_time: startTime,
      end_time: endTime,
      num_buckets: numBuckets,
      x_axis: xAxis,
    })
  );
}

export function fetchMomentUsage({
  sessionId,
  momentId,
}: {
  sessionId: string;
  momentId: number;
}): Promise<MomentUsage> {
  return getAPI(`/api/sessions/${sessionId}/moments/${momentId}/usage`);
}

export function fetchSessionTokenUsage(
  sessionId: string
): Promise<SessionTokenUsage> {
  return getAPI(`/api/sessions/${sessionId}/token-usage`);
}

export function fetchSessionParents(
  sessionId: string
): Promise<Array<SessionRange>> {
  return getAPI(`/api/sessions/${sessionId}/parents`);
}

export function fetchSessionChildren(
  sessionId: string
): Promise<SessionTreeNode> {
  return getAPI(`/api/sessions/${sessionId}/children`);
}

export function fetchBackendServices(): Promise<BackendServiceMap> {
  return getAPI("/api/backend-services");
}

export function fetchServiceAliases(): Promise<Array<ServiceAlias>> {
  return getAPI("/api/service-aliases");
}

export function fetchServiceAlias(
  serviceAliasId: string
): Promise<ServiceAlias> {
  return getAPI(`/api/service-aliases/${serviceAliasId}`);
}

export function createServiceAlias(
  serviceAlias: EditServiceAlias
): Promise<ServiceAlias> {
  return postAPI("/api/service-aliases", serviceAlias);
}

export function updateServiceAlias(
  serviceAlias: EditServiceAlias
): Promise<void> {
  return putAPI<void>(
    `/api/service-aliases/${serviceAlias.uuid}`,
    serviceAlias
  );
}

export function deleteServiceAlias(serviceAliasId: string): Promise<void> {
  return deleteAPI<void>(`/api/service-aliases/${serviceAliasId}`);
}

export function fetchOrganizationAuthorizedServiceAliases(
  organization: string
): Promise<Array<string>> {
  return getAPI(`/api/organizations/${organization}/authorized-aa-services`);
}

export function authorizeServiceAliasForOrganization({
  organization,
  serviceAliasId,
}: {
  organization: string;
  serviceAliasId: string;
}): Promise<void> {
  return postAPI(
    `/api/organizations/${organization}/authorized-aa-services`,
    serviceAliasId
  );
}

export function deauthorizeServiceAliasForOrganization({
  organization,
  serviceAliasId,
}: {
  organization: string;
  serviceAliasId: string;
}): Promise<void> {
  return deleteAPI(
    `/api/organizations/${organization}/authorized-aa-services/${serviceAliasId}`
  );
}

export function fetchOrganizationServices(
  organizationId: string
): Promise<Array<OrganizationService>> {
  return getAPI(`/api/organizations/${organizationId}/services`);
}

export function fetchUsersWidgetToken(): Promise<{ token: string }> {
  return getAPI("/api/auth/user-table-widget-token");
}

export function fetchAgentCloneTree({
  sessionId,
  agentId,
}: {
  sessionId: string;
  agentId: string;
}): Promise<AgentCloneTree> {
  return getAPI(`/api/sessions/${sessionId}/agents/${agentId}/clones`);
}

export function fetchProjectAgencyTags({
  organization,
  projectId,
}: {
  organization: string;
  projectId: string;
}): Promise<Page<AgencyTagType>> {
  return getAPI(
    `/api/organizations/${organization}/projects/${projectId}/tags`
  );
}

export function createOrUpdateRating({
  sessionId,
  momentId,
  eventId,
  rating,
}: {
  sessionId: string;
  momentId: number;
  eventId: string;
  rating: RatingType;
}): Promise<RateMomentResponse> {
  const url = `/api/sessions/${sessionId}/moments/${momentId}/events/${eventId}/rating`;
  return postAPI(url, rating);
}
export function deleteRating({ eventId }: { eventId: string }): Promise<void> {
  const url = `/api/events/${eventId}/rating`;
  return deleteAPI(url);
}

// -- Cross-org model usage --------------------------------------------------

export async function fetchUsageByModel({
  startsAfter = null,
  scope = "monthly",
  beginsWith,
  between,
  search,
}: {
  startsAfter?: string | null;
  scope?: "daily" | "monthly";
  beginsWith?: string;
  between?: string;
  search?: string | null;
}): Promise<PagedModelUsage> {
  return getAPI(
    buildQueryString("/api/usage-by-model", {
      scope,
      begins_with: beginsWith || undefined,
      between: between || undefined,
      search: search || undefined,
      starting_after: startsAfter || undefined,
    })
  );
}

export async function fetchUsageByModelBreakdown({
  serviceAliasUid,
  startsAfter = null,
  scope = "monthly",
  beginsWith,
  between,
}: {
  serviceAliasUid: string;
  startsAfter?: string | null;
  scope?: "daily" | "monthly";
  beginsWith?: string;
  between?: string;
}): Promise<PagedModelUsageBreakdown> {
  return getAPI(
    buildQueryString(
      `/api/usage-by-model/${encodeURIComponent(serviceAliasUid)}/breakdown`,
      {
        scope,
        begins_with: beginsWith || undefined,
        between: between || undefined,
        starting_after: startsAfter || undefined,
      }
    )
  );
}

export function getCommonAPI(): CommonAPI {
  return {
    createOrUpdateRating,
    deleteRating,
    fetchAgentDetails,
    fetchAgentMomentList,
    fetchAgentPublicState,
    fetchAgentPresentation,
    fetchSession,
    fetchEventStream,
    fetchEventStreamDetail,
    fetchMessageTypes,
    fetchAPIRequest,
    fetchAPIKeys: fetchOrganizationAPIKeys,
    fetchSessionStats,
    fetchSessionTokenUsage,
    fetchMomentUsage,
    fetchSessionParents,
    fetchSessionChildren,
    fetchUsersWidgetToken,
    fetchAgentCloneTree,
    fetchProjectAgencyTags,
    fetchUsageMetrics,
    fetchOrganizationUsageRecent,
    fetchProjectUsageRecent,
    fetchOrganizationUsageBetween,
    fetchProjectUsageBetween,
  };
}
